import { useMemo, useEffect } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import { generateCurvedPath } from "./curvedPolyline";

export const calculateOptimalZoom = (events: any[]): number => {
    if (events.length === 0) return 2;
    if (events.length === 1) return 12;

    // Find min/max coordinates
    const lats = events.map(e => e.latitude);
    const lngs = events.map(e => e.longitude);

    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    // Calculate the span of coordinates
    const latSpan = maxLat - minLat;
    const lngSpan = maxLng - minLng;
    const maxSpan = Math.max(latSpan, lngSpan);

    // Enhanced zoom levels for better international coverage
    if (maxSpan < 0.01) return 15;      // Very close (~1km) - Street level
    if (maxSpan < 0.05) return 13;      // Close (~5km) - Neighborhood
    if (maxSpan < 0.1) return 12;       // Near (~10km) - City district
    if (maxSpan < 0.5) return 10;       // Medium (~50km) - City
    if (maxSpan < 1) return 9;          // Large (~100km) - Metropolitan
    if (maxSpan < 2) return 8;          // Very large (~200km) - Region
    if (maxSpan < 5) return 7;          // State level (~500km)
    if (maxSpan < 10) return 6;         // Small country level (~1000km)
    if (maxSpan < 20) return 5;         // Large country level (~2000km)
    if (maxSpan < 40) return 4;         // Continent level (~4000km)
    if (maxSpan < 70) return 3;         // Multi-continent level (~7000km)
    return 2;                            // World level (maximum zoom out)
};

export const EventMarker = ({ isLast }: { isLast: boolean }) => {
    const imageUrl = isLast
        ? `${process.env.PUBLIC_URL}/images/Tralexho-X.svg`
        : `${process.env.PUBLIC_URL}/images/Tralexho-O.svg`;

    return (
        <div style={{
            width: '20px',
            height: '20px',
            marginTop: '20px'
        }}>
            <img
                src={imageUrl}
                alt={isLast ? 'End Event' : 'Event'}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                }}
            />
        </div>
    );
};

// Component to draw curved polylines
export const CurvedPolylines = ({ events }: { events: any }) => {
    const map = useMap();

    useMemo(() => {
        if (!map || events.length < 2) return;

        // Clear existing polylines
        const existingPolylines = (window as any).curvedPolylines || [];
        existingPolylines.forEach((polyline: google.maps.Polyline) => polyline.setMap(null));

        const polylines: google.maps.Polyline[] = [];

        for (let i = 0; i < events.length - 1; i++) {
            const start = { lat: events[i].latitude, lng: events[i].longitude };
            const end = { lat: events[i + 1].latitude, lng: events[i + 1].longitude };

            // Generate curved path (more segments for smoother curves)
            const path = generateCurvedPath(start, end, 0.3, 100);

            // Create polyline with elegant arrow showing direction
            const arrowSymbol = {
                path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                scale: 3.5,
                strokeColor: '#000',
                strokeWeight: 0.5,
                fillColor: '#000',
                fillOpacity: 0.9,
            };

            const polyline = new google.maps.Polyline({
                path: path,
                geodesic: false,
                strokeColor: 'red',
                strokeWeight: 2,
                icons: [
                    {
                        icon: arrowSymbol,
                        offset: '50%',
                    },
                ],
            });

            polyline.setMap(map);
            polylines.push(polyline);
        }

        (window as any).curvedPolylines = polylines;
    }, [map, events]);

    return null;
};

// Component to handle map zoom when location is selected
export const MapController = ({
    selectedLocation,
    events,
    onReady
}: {
    selectedLocation: number | null;
    events: any[];
    onReady: (map: google.maps.Map) => void;
}) => {
    const map = useMap();

    useEffect(() => {
        if (map) {
            onReady(map);
        }
    }, [map, onReady]);

    useEffect(() => {
        if (map && selectedLocation !== null && events[selectedLocation]) {
            const event = events[selectedLocation];
            map.panTo({ lat: event.latitude, lng: event.longitude });
            setTimeout(() => {
                map.setZoom(15);
            }, 300);
        }
    }, [map, selectedLocation, events]);

    return null;
};
